// Chi tiết lệnh: trạng thái gộp, tổng hợp, bảng lần chạy theo bot, Duyệt tất cả, Huỷ lệnh, hộp canary hỏi. Cập nhật tức thời từ run.changed/job.changed.
import { api, errMsg } from "../api";
import { busy, clear, confirmBox, gen, h, toast } from "../dom";
import { fmtDuKien, fmtTime, jobAlive, jobCoCanhBao, jobNhanHien, jobStatusKind, jobStatusTitle, modeTagClass, modeText, modeTitle, runCoCanhBao, runNhanHien, runStatusTitle } from "../format";
import type { Job, RunSummary } from "../types";
import { uiSocket } from "../ws";
import { ganLyDo, laMaKbSoDo, layTenKb, tenBuoc, tenKb } from "../ten-kb"; // L-78 / N9: TÊN kịch bản, TÊN bước thay mã
import { isSuper, store } from "../store";

/**
 * N11: nút quay lại của "Tiến độ lần chạy" — kịch bản sơ đồ (kb-…) → "← <tên kịch bản>"; Chế độ kỹ thuật → Danh sách lệnh chạy;
 * còn lại → Lịch sử chạy (trang Lệnh chạy không còn trên menu Boss).
 */
function nutQuayLai(bundleName: string | null): HTMLAnchorElement {
  if (store.kyThuat) return h("a", { href: "#/jobs", title: "Về danh sách lệnh chạy (Chế độ kỹ thuật)", "data-testid": "job-quay-lai" }, "← Danh sách lệnh chạy");
  const a = h("a", { href: "#/history", title: "Về Lịch sử chạy", "data-testid": "job-quay-lai" }, "← Lịch sử chạy");
  if (bundleName && laMaKbSoDo(bundleName)) layTenKb(bundleName, (v) => {
    if (!v) return;
    a.href = `#/kich-ban/${encodeURIComponent(v.id)}`;
    a.title = "Về trang kịch bản này";
    a.textContent = `← ${v.ten}`;
  });
  return a;
}

export async function renderJobDetail(root: HTMLElement, params: Record<string, string>): Promise<() => void> {
  const alive = gen("job-detail");
  const id = params.id ?? "";
  let job: Job;
  try {
    job = await api<Job>("GET", `/api/jobs/${encodeURIComponent(id)}`);
  } catch (e) {
    if (!alive()) return () => {};
    root.append(h("div", { class: "err-box", "data-testid": "page-error" }, "Không tải được lệnh: ", errMsg(e)), h("p", {}, nutQuayLai(null)));
    return () => {};
  }
  if (!alive()) return () => {};

  const head = h("div", { class: "card" });
  const canaryBox = h("div", { class: "card warn-box", hidden: true, "data-testid": "canary-box" });
  const tbody = h("tbody", { "data-testid": "run-rows" });
  const rows = new Map<string, HTMLTableRowElement>();
  root.append(
    h("p", { class: "small" }, nutQuayLai(job.spec.bundleName)),
    h("p", { class: "small muted eyebrow", "data-testid": "job-tieu-de" }, "Tiến độ lần chạy"),
    head, canaryBox,
    h("div", { class: "tbl" }, h("table", {},
      h("thead", {}, h("tr", {}, h("th", {}, "Bot"), h("th", {}, "Trạng thái"), h("th", { title: "Bước đang chờ bạn duyệt (nếu có)" }, "Bước / chờ"), h("th", { title: "Lý do lỗi/dừng/bỏ qua do máy chủ hoặc bot báo" }, "Lý do"), h("th", { title: "Giờ dự kiến bắt đầu sau giãn cách" }, "Dự kiến"), h("th", {}, "Bắt đầu"), h("th", {}, "Kết thúc"), h("th", {}))),
      tbody)));

  const approveAll = h("button", { type: "button", class: "ok", title: "Duyệt mọi bước đang chờ duyệt trong lệnh này — các bot sẽ thực hiện bước rủi ro cao ngay", "data-testid": "approve-all" }, "Duyệt tất cả");
  const cancelBtn = h("button", { type: "button", class: "danger", title: "Huỷ lệnh: lần chạy chưa bắt đầu bị huỷ, lần chạy đang chạy bị dừng — có hộp xác nhận", "data-testid": "cancel-job" }, "Huỷ lệnh");
  approveAll.addEventListener("click", () => void busy(approveAll, "Đang duyệt…", async () => {
    try {
      const r = await api<{ approved: number }>("POST", `/api/jobs/${encodeURIComponent(id)}/approve-all`);
      toast(r.approved ? `Đã duyệt ${r.approved} lần chạy` : "Không có lần chạy nào đang chờ duyệt");
    } catch (e) { toast(errMsg(e), "err"); }
  }));
  cancelBtn.addEventListener("click", async () => {
    if (!(await confirmBox({ title: "Huỷ lệnh này?", text: "Lần chạy chưa bắt đầu bị huỷ; lần chạy đang chạy bị dừng.", okText: "Huỷ lệnh", okTitle: "Huỷ lệnh ngay trên mọi bot", danger: true }))) return;
    await busy(cancelBtn, "Đang huỷ…", async () => {
      try { job = await api<Job>("POST", `/api/jobs/${encodeURIComponent(id)}/cancel`); paintHead(); paintRows(); toast("Đã huỷ lệnh"); }
      catch (e) { toast(errMsg(e), "err"); }
    });
  });

  function paintHead(): void {
    clear(head);
    const s = job.spec, sm = job.summary;
    const pendingN = job.runs.filter((r) => r.status === "paused_approval").length;
    head.append(h("div", {},
      h("div", { class: "row between" },
        h("div", { class: "grow" },
          h("h1", { style: "margin:0" }, tenKb(s.bundleName, { lienKet: true }), " ", h("span", { class: modeTagClass(s.mode), title: modeTitle(s.mode) }, modeText(s.mode)), " ", h("span", { class: "tag", title: s.kind === "chia_deu" ? "Một danh sách chia đều cho các bot" : "Mọi bot cùng tham số" }, s.kind === "chia_deu" ? "chia đều" : "giống nhau")),
          h("div", { class: "small muted", title: `Mã lệnh: ${job.id}`, "data-testid": "job-meta" }, `${isSuper() ? `Lệnh ${job.id} · ` : ""}Tạo ${fmtTime(job.createdAt)} bởi ${job.createdByName ?? job.createdBy}${job.endedAt ? ` · kết thúc ${fmtTime(job.endedAt)}` : ""}`)),
        h("span", { class: `tag ${jobNhanHien(job).kind}`, style: "font-size:13px;padding:4px 10px", title: jobCoCanhBao(job) ? "Mọi bot đã chạy xong nhưng có lần chạy không làm được việc chính — xem lý do" : jobStatusTitle(job.status), "data-testid": "job-status" }, jobNhanHien(job).text)),
      job.reason ? ((p) => { ganLyDo(p, s.bundleName, job.reason); return p; })(h("p", { class: `wrap ${jobStatusKind(job.status) === "err" ? "err" : jobCoCanhBao(job) ? "warn" : ""}`, "data-testid": "job-ly-do" })) : null,
      h("div", { class: "row small muted", "data-testid": "job-summary" },
        h("span", { title: "Tổng số bot trong lệnh" }, `${sm.total} bot`), h("span", { class: "ok", title: "Số bot đã chạy xong" }, `${sm.done} xong`), h("span", { class: "err", title: "Số bot lỗi, bị trang chặn / từ chối, hết giờ hoặc mất liên lạc" }, `${sm.failed} lỗi`),
        h("span", { class: "running", title: "Số bot đang chạy" }, `${sm.running} đang chạy`), h("span", { class: "warn", title: "Số bot đang dừng chờ bạn duyệt bước hoặc nhập OTP" }, `${sm.waiting} chờ duyệt`), h("span", { title: "Số bot đang chờ giãn cách hoặc chờ đến lượt" }, `${sm.queued} xếp hàng`)),
      h("div", { class: "row small muted" },
        h("span", { title: "Mỗi bot bắt đầu lệch ngẫu nhiên tối đa chừng này" }, `giãn cách ≤ ${s.gianCachS} giây`), h("span", { title: "Chạy 1 bot trước: xong không lỗi mới phát cho các bot còn lại" /* đặc tả đổi 08/10/2026 (bỏ Nháp): trước "Chạy thử trên 1 bot trước" */ }, s.canary.on ? `chạy 1 bot trước${s.canary.botId ? `: ${nameOf(s.canary.botId)}` : ""}` : "phát cùng lúc cho mọi bot"),
        h("span", { title: s.autoApprove ? "Bot tự làm bước gửi/đăng/xoá không hỏi" : "Bot dừng chờ bạn duyệt ở bước gửi/đăng/xoá" }, s.autoApprove ? "tự duyệt bước rủi ro cao" : "chờ duyệt bước rủi ro cao"), h("span", { title: "Quá thời gian này lần chạy bị đánh dấu Hết giờ" }, `hết giờ ${s.timeoutMin} phút`),
        Object.keys(s.params).length ? h("span", {}, "tham số: ", h("code", {}, Object.entries(s.params).map(([k, v]) => `${k}=${v}`).join(", "))) : null,
        s.kind === "chia_deu" ? h("span", {}, `${s.list?.length ?? 0} phần tử → ${s.listParam}`) : null),
      jobAlive(job.status) ? h("div", { class: "row", style: "margin-top:8px" }, pendingN ? approveAll : h("button", { type: "button", class: "ok", disabled: true, title: "Chưa có bước nào đang chờ duyệt" }, "Duyệt tất cả"), cancelBtn) : null));
    canaryBox.hidden = job.status !== "canary_hoi";
    if (job.status === "canary_hoi") {
      clear(canaryBox);
      const cont = h("button", { type: "button", class: "pri", title: "Phát tiếp lệnh cho các bot còn lại dù bot chạy trước bị trang từ chối", "data-testid": "canary-continue" }, "Tiếp tục");
      const stop = h("button", { type: "button", class: "danger", title: "Huỷ lệnh, không phát cho các bot còn lại", "data-testid": "canary-cancel" }, "Huỷ");
      cont.addEventListener("click", () => void busy(cont, "…", () => canaryDecide(true)));
      stop.addEventListener("click", () => void busy(stop, "…", () => canaryDecide(false)));
      canaryBox.append(h("b", {}, "Bot chạy trước bị trang web chặn"), h("p", { class: "small" }, job.reason ?? "Trang web không nhận việc bot chạy trước vừa làm. Có thể là kết quả đúng (ví dụ tài khoản chưa là thành viên nhóm). Phát tiếp cho các bot còn lại?"),
        h("div", { class: "row" }, cont, stop));
    }
  }
  async function canaryDecide(cont: boolean): Promise<void> {
    try { job = await api<Job>("POST", `/api/jobs/${encodeURIComponent(id)}/canary-continue`, { continue: cont }); paintHead(); paintRows(); toast(cont ? "Đã phát tiếp cho các bot còn lại" : "Đã huỷ lệnh"); }
    catch (e) { toast(errMsg(e), "err"); }
  }
  function nameOf(botId: string): string {
    return job.runs.find((r) => r.botId === botId)?.botName ?? botId;
  }

  function row(r: RunSummary): HTMLTableRowElement {
    const tr = h("tr", { "data-testid": "run-row", dataset: { key: r.key, status: r.status } },
      h("td", { class: "name" }, h("span", { title: r.botName }, h("b", {}, r.botName))),
      h("td", {}, h("span", { class: `tag ${runNhanHien(r).kind}`, title: runCoCanhBao(r) ? "Bot chạy xong nhưng không làm được việc chính — xem cột lý do" : runStatusTitle(r.status), "data-testid": "run-status" }, runNhanHien(r).text)),
      h("td", { class: "small reason" }, r.pending ? h("span", { class: "warn wrap", "data-testid": "run-cho-buoc" }, tenBuoc(r.bundleName, r.pending.stepId), `: ${r.pending.description}`) : ""),
      ((td) => { ganLyDo(td, r.bundleName, r.reason); return td; })(h("td", { class: `reason small wrap${runCoCanhBao(r) ? " warn" : ""}`, "data-testid": "run-ly-do" })),
      h("td", { class: "nowrap small", "data-testid": "run-du-kien" }, fmtDuKien(r)),
      h("td", { class: "nowrap small" }, fmtTime(r.startedAt)),
      h("td", { class: "nowrap small" }, fmtTime(r.endedAt)),
      h("td", { class: "nowrap" },
        r.status === "paused_approval" ? h("button", { type: "button", class: "ok mini", title: "Cho phép bot này thực hiện bước đang chờ rồi chạy tiếp", "data-testid": "run-approve", onclick: (e: Event) => void approveOne(r, e.currentTarget as HTMLButtonElement) }, "Duyệt") : null, " ",
        r.runId ? h("a", { class: "btn mini", href: `#/runs/${encodeURIComponent(r.key)}`, title: "Mở chi tiết lần chạy: nhật ký từng bước, ảnh chờ duyệt, dừng", "data-testid": "run-link" }, "Chi tiết") : null));
    return tr;
  }
  async function approveOne(r: RunSummary, btn: HTMLButtonElement): Promise<void> {
    await busy(btn, "…", async () => {
      try { await api("POST", `/api/runs/${encodeURIComponent(r.key)}/approve`, { approved: true }); toast(`Đã duyệt ${r.botName}`); }
      catch (e) { toast(errMsg(e), "err"); }
    });
  }
  function paintRows(): void {
    const want = new Set(job.runs.map((r) => r.key));
    for (const [k, tr] of rows) if (!want.has(k)) { tr.remove(); rows.delete(k); }
    let prev: HTMLElement | null = null;
    for (const r of job.runs) {
      const sig = JSON.stringify([r.status, r.reason, r.plannedAt, r.startedAt, r.endedAt, r.pending?.stepId, r.botName]);
      let tr = rows.get(r.key);
      if (!tr || tr.dataset.sig !== sig) {
        const fresh = row(r);
        fresh.dataset.sig = sig;
        if (tr) tr.replaceWith(fresh); else tbody.insertBefore(fresh, prev ? prev.nextSibling : tbody.firstChild);
        tr = fresh;
        rows.set(r.key, tr);
      }
      prev = tr;
    }
    if (!job.runs.length && !tbody.children.length) tbody.append(h("tr", {}, h("td", { colspan: 8, class: "muted" }, "Lệnh này không có lần chạy nào.")));
  }
  function applyRun(r: RunSummary): void {
    const i = job.runs.findIndex((x) => x.key === r.key);
    if (i >= 0) job.runs[i] = r; else job.runs.push(r);
    // Tính lại tổng hợp tại chỗ để số liệu không lệch trong lúc chờ job.changed.
    const sm = { total: job.runs.length, done: 0, failed: 0, waiting: 0, running: 0, queued: 0 };
    for (const x of job.runs) {
      if (x.status === "done") sm.done++;
      else if (x.status === "failed" || x.status === "halted" || x.status === "het_gio" || x.status === "mat_lien_lac") sm.failed++;
      else if (x.status === "paused_approval" || x.status === "paused_otp") sm.waiting++;
      else if (x.status === "running") sm.running++;
      else if (x.status === "cho_gian_cach" || x.status === "cho_den_luot") sm.queued++;
    }
    job.summary = sm;
  }

  paintHead();
  paintRows();
  const off = uiSocket.on((ev) => {
    if (ev.type === "run.changed" && ev.run.jobId === job.id) { applyRun(ev.run); paintRows(); paintHead(); }
    else if (ev.type === "job.changed" && ev.job.id === job.id) { job = { ...ev.job, runs: ev.job.runs?.length ? ev.job.runs : job.runs }; paintHead(); paintRows(); }
  });
  return off;
}
